import React, { useEffect } from "react"
import { Grid, Box, CircularProgress } from "@mui/material"
import { FlagsIconSeeker } from "@ui-seeker/core"
import { getExcel, translateCountryName } from "@ui-seeker/utils"
import PageWrapper from "Pages/@PageWrapper"
import { AppCardTitle, AppCrudTable, AppToolbarCreateButton, AppButtonIcon, AppResultsSkeleton } from "@components"
import AppOfficesBlockContainer from "@components/DataBlocks/OfficesContainer"
import { BACKEND_APIS } from "@constants"
import { AUTO_EUROPE_TOOL_ROUTES } from "@constants/routes"
import {
	autoEuropePricingIdDrawerData,
	autoEuropePricingIdListData,
	autoEuropePricingIdIsLoading,
	autoEuropePricingIdIsSubmitting,
} from "Pages/AutoEuropeTool/PricingId/@signals"

import AutoEuropePricingIdDrawer from "Pages/AutoEuropeTool/PricingId/Drawer"
import useAutoEuropePricingIdTable from "Pages/AutoEuropeTool/PricingId/@hooks/useTable"
import useAutoEuropePricingIdFormSubmit from "Pages/AutoEuropeTool/PricingId/@hooks/useFormSubmit"
import useAutoEuropePricingIdActions from "Pages/AutoEuropeTool/PricingId/@hooks/useActions"
import useAutoEuropePricingIdEdit from "Pages/AutoEuropeTool/PricingId/@hooks/useEdit"

const EMPTY_FORM = { offices: [], market: null, pricingRecordId: null, autoeuropeVsMetaId: null, cr8VsMetaId: null }

const centered = v => <Box sx={{ display: 'flex', justifyContent: 'center' }}>{v}</Box>

const textFilterLogic = (value, filters) => {
	if (!filters?.[0]) return false
	return !String(value ?? '').toLowerCase().includes(String(filters[0]).toLowerCase())
}

const arrayTextFilterLogic = (arr, filters) => {
	if (!filters?.[0]) return false
	if (!Array.isArray(arr) || arr.length === 0) return true
	const q = String(filters[0]).toLowerCase()
	return !arr.some(item => String(item).toLowerCase().includes(q))
}

const MARKET_ALIASES = { gb: ['uk'] }

const marketFilterLogic = (value, filters) => {
	if (!filters?.[0]) return false
	const q = String(filters[0]).toLowerCase()
	if (!value) return !('any'.includes(q) || 'null'.includes(q))
	const code = String(value).toLowerCase()
	const label = String(translateCountryName(value)).toLowerCase()
	const aliases = MARKET_ALIASES[code] || []
	return !(label.includes(q) || code.includes(q) || aliases.some(a => a.includes(q)))
}

function ActionsCell({ row, onDelete }) {
	const { onEdit, isLoading: isEditLoading } = useAutoEuropePricingIdEdit(row.pricingRecordId)
	return (
		<Box sx={{ display: 'flex', justifyContent: 'center' }}>
			{isEditLoading
				? <CircularProgress size={17} sx={{ m: 0.2 }}/>
				: <AppButtonIcon icon="pen" tooltip="Edit" onClick={e => { e.stopPropagation(); onEdit() }}/>
			}
			<AppButtonIcon
				icon="trash-can"
				tooltip="Delete"
				onClick={e => { e.stopPropagation(); onDelete(row.pricingRecordId) }}
			/>
		</Box>
	)
}

export default function AutoEuropePricingIdPage() {
	const { getTableData } = useAutoEuropePricingIdTable()
	const { onSubmit } = useAutoEuropePricingIdFormSubmit({ getTableData })
	const { onDelete } = useAutoEuropePricingIdActions()

	useEffect(() => {
		getTableData()
	}, [])

	return <PageWrapper
		head={`DS Rentacar | ${AUTO_EUROPE_TOOL_ROUTES.SUBTITLE}`}
		title={AUTO_EUROPE_TOOL_ROUTES.PRICING_ID_TITLE}
		subtitle={AUTO_EUROPE_TOOL_ROUTES.PRICING_ID_SUBTITLE}
	>
		<AutoEuropePricingIdDrawer onSubmit={onSubmit} isLoading={autoEuropePricingIdIsSubmitting.value}/>
		<Grid container spacing={2}>
			<Grid item xs={12}>
				{autoEuropePricingIdIsLoading.value
					? <AppResultsSkeleton rows={7} columns={6}/>
					: <AppCrudTable
						title={<AppCardTitle icon="id-card" title="Pricing ID Configuration"/>}
						data={autoEuropePricingIdListData.value}
						columns={[
							{ name: 'pricingRecordId', label: 'Pricing Record ID', options: { filterType: 'textField', filterOptions: { logic: textFilterLogic }, customBodyRowRender: v => centered(v) } },
							{ name: 'officesNameFilter', label: 'Offices', options: { display: 'excluded', filterType: 'textField', filterOptions: { logic: arrayTextFilterLogic } } },
							{ name: 'officesName', label: 'Offices', options: { filter: false, customBodyRowRenderLite: v => centered(<AppOfficesBlockContainer rows={v}/>) } },
							{ name: 'market', label: 'Site Market', options: { filterType: 'textField', filterOptions: { logic: marketFilterLogic }, customBodyRowRender: v => centered(v ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><span>{translateCountryName(v)}</span><FlagsIconSeeker countryName={v}/></Box> : 'Any') } },
							{ name: 'autoeuropeVsMetaId', label: 'AE vs Meta ID', options: { filterType: 'textField', filterOptions: { logic: textFilterLogic }, customBodyRowRender: v => centered(v ?? '—') } },
							{ name: 'cr8VsMetaId', label: 'CR8 vs Meta ID', options: { filterType: 'textField', filterOptions: { logic: textFilterLogic }, customBodyRowRender: v => centered(v ?? '—') } },
							{ name: 'pricingRecordId', label: 'Actions', options: { filter: false, customBodyRowRender: (_, row) => <ActionsCell row={row} onDelete={onDelete}/> } },
						]}
						options={{
							customToolbar: () => <AppToolbarCreateButton
								label="Create"
								icon="file-plus"
								onClick={() => autoEuropePricingIdDrawerData.value = { ...EMPTY_FORM }}
								/>,
							onDownload: () => getExcel(`${BACKEND_APIS.AUTOEUROPE_PRICING_TOOL}/pricing-id/list/excel`, {}),
						}}
					/>
				}
			</Grid>
		</Grid>
	</PageWrapper>
}